import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import LogoutButton from '../Auth/LogoutButton';
import {
    ContentContainer,
    Heading,
    MediaList,
    PageContainer,
    Sidebar,
    SidebarItem
} from "./StyledComponents";
import {fetchPublicVideoLinks, fetchThumbnailsByLinkIds} from "../Network/ServiceMethods";
import CopyableLink from "../Shared/CopyableLink";
import {LinkControls, MediaCard, MediaDetails, MediaThumbnail} from "./StyledComponents";
import {Thumbnail} from "./Thumbnail";
import Pagination from "../Shared/Pagination";
import {useNavigate} from "react-router-dom";
import VideoPlayerModal from "./VideoPlayerModal";

const PlayOverlay = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 48px;
    height: 48px;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    transition: all 0.2s;
    backdrop-filter: blur(4px);

    &::before {
        content: '';
        width: 0;
        height: 0;
        border-left: 16px solid white;
        border-top: 10px solid transparent;
        border-bottom: 10px solid transparent;
        margin-left: 4px;
    }

    @media (max-width: 768px) {
        width: 64px;
        height: 64px;

        &::before {
            border-left: 20px solid white;
            border-top: 14px solid transparent;
            border-bottom: 14px solid transparent;
            margin-left: 5px;
        }
    }
`;

const ThumbnailWrapper = styled.div`
    position: relative;
    display: inline-block;

    &:hover ${PlayOverlay} {
        background: rgba(0, 123, 255, 0.9);
        transform: translate(-50%, -50%) scale(1.1);
    }

    &:active ${PlayOverlay} {
        transform: translate(-50%, -50%) scale(0.95);
    }
`;

function PublicVideos({isPublic = false}) {
    const [videoLinks, setVideoLinks] = useState([]);
    const [thumbnails, setThumbnails] = useState({});
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedVideoId, setSelectedVideoId] = useState(null);
    const [selectedVideoIndex, setSelectedVideoIndex] = useState(null);
    const navigate = useNavigate();

    const loadVideoLinks = async (page, size) => {
        setIsLoading(true);
        try {
            const pageData = await fetchPublicVideoLinks(page, size);
            setVideoLinks(pageData.content);
            setTotalPages(pageData.totalPages);
            setTotalElements(pageData.totalElements);
            setCurrentPage(pageData.number);

            // Fetch thumbnails for the loaded links
            if (pageData.content && pageData.content.length > 0) {
                const linkIds = pageData.content.map(link => link.id);
                const thumbData = await fetchThumbnailsByLinkIds(linkIds);
                setThumbnails(thumbData);
            }
        } catch (err) {
            console.error("Failed to load public video links:", err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadVideoLinks(currentPage, pageSize);
    }, [currentPage, pageSize]);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(0);
    };

    const handleVideoClick = (linkId, index) => {
        setSelectedVideoId(linkId);
        setSelectedVideoIndex(index);
    };

    const handleCloseModal = () => {
        setSelectedVideoId(null);
        setSelectedVideoIndex(null);
    };

    const handleNextVideo = async () => {
        if (selectedVideoIndex === null) return;

        const nextIndex = selectedVideoIndex + 1;

        // Check if we need to load the next page
        if (nextIndex >= videoLinks.length) {
            // We're at the end of current page
            if (currentPage < totalPages - 1) {
                // Load next page
                await loadVideoLinks(currentPage + 1, pageSize);
                // After loading, select the first video of the new page
                setSelectedVideoIndex(0);
                // The video ID will be updated after videoLinks updates
            }
        } else {
            // Move to next video in current page
            setSelectedVideoIndex(nextIndex);
            setSelectedVideoId(videoLinks[nextIndex].id);
        }
    };

    const handlePreviousVideo = async () => {
        if (selectedVideoIndex === null) return;

        const prevIndex = selectedVideoIndex - 1;

        // Check if we need to load the previous page
        if (prevIndex < 0) {
            // We're at the beginning of current page
            if (currentPage > 0) {
                // Set a flag to select last video after page loads
                setSelectedVideoIndex(-1);
                // Load previous page
                await loadVideoLinks(currentPage - 1, pageSize);
            }
        } else {
            // Move to previous video in current page
            setSelectedVideoIndex(prevIndex);
            setSelectedVideoId(videoLinks[prevIndex].id);
        }
    };

    // Handle selecting last video when navigating to previous page
    useEffect(() => {
        if (selectedVideoIndex === -1 && videoLinks.length > 0) {
            const lastIndex = videoLinks.length - 1;
            setSelectedVideoIndex(lastIndex);
            setSelectedVideoId(videoLinks[lastIndex].id);
        }
    }, [videoLinks]);

    // Update selected video ID when moving to a new page
    useEffect(() => {
        if (selectedVideoIndex !== null && videoLinks.length > 0 && selectedVideoIndex < videoLinks.length) {
            setSelectedVideoId(videoLinks[selectedVideoIndex].id);
        }
    }, [currentPage, videoLinks, selectedVideoIndex]);

    const hasNext = selectedVideoIndex !== null &&
        (selectedVideoIndex < videoLinks.length - 1 || currentPage < totalPages - 1);

    const hasPrevious = selectedVideoIndex !== null &&
        (selectedVideoIndex > 0 || currentPage > 0);

    return (
        <PageContainer>
            {/* Sidebar Navigation */}
            {!isPublic && (
                <Sidebar>
                    <SidebarItem onClick={() => navigate('/dashboard')}>Dashboard</SidebarItem>
                    <SidebarItem onClick={() => navigate('/public-videos')}>Public Videos</SidebarItem>
                    <LogoutButton/>
                </Sidebar>
            )}

            {/* Main Content */}
            <ContentContainer>
                <Heading>Public Videos</Heading>

                {/* Public video links list */}
                {isLoading ? (
                    <div>Loading public videos...</div>
                ) : videoLinks.length > 0 ? (
                    <>
                        <MediaList>
                            {videoLinks.map((link, index) => (
                                <MediaCard key={index}>
                                    <MediaThumbnail onClick={() => handleVideoClick(link.id, index)} style={{cursor: 'pointer'}}>
                                        <ThumbnailWrapper>
                                            <Thumbnail url={thumbnails[link.id]} />
                                            <PlayOverlay />
                                        </ThumbnailWrapper>
                                    </MediaThumbnail>

                                    <MediaDetails>
                                        <div>
                                            {link.description || link.name || "Unnamed Video"} &nbsp;
                                            ({Math.round(link.size / 1024)} KB)
                                        </div>

                                        <LinkControls>
                                            <CopyableLink linkId={link.id} />
                                        </LinkControls>
                                    </MediaDetails>
                                </MediaCard>
                            ))}
                        </MediaList>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalElements={totalElements}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </>
                ) : (
                    <div>No public videos available.</div>
                )}
            </ContentContainer>

            {selectedVideoId && (
                <VideoPlayerModal
                    linkId={selectedVideoId}
                    onClose={handleCloseModal}
                    onNext={handleNextVideo}
                    onPrevious={handlePreviousVideo}
                    hasNext={hasNext}
                    hasPrevious={hasPrevious}
                />
            )}
        </PageContainer>
    );
}

export default PublicVideos;